import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, Filter, Anchor, Utensils, Waves, Search, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../App';

const ToursPage = () => {
  const [tours, setTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await api.get('/tours');
        setTours(response.data);
        setFilteredTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  useEffect(() => {
    let filtered = tours;

    if (category !== 'all') {
      filtered = filtered.filter(tour => tour.category === category);
    }

    if (searchQuery) {
      filtered = filtered.filter(tour =>
        tour.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTours(filtered);
  }, [category, searchQuery, tours]);

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'boating': return <Anchor className="w-4 h-4" />;
      case 'food': case 'restaurant': return <Utensils className="w-4 h-4" />;
      case 'snorkel': return <Waves className="w-4 h-4" />;
      default: return <Anchor className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      boating: 'Boat Tour',
      food: 'Food Tour',
      snorkel: 'Beach & Snorkel',
      bar: 'Beach Bar',
      restaurant: 'Dining',
      combo: 'Combo Tour'
    };
    return labels[cat] || cat;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-muted rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-2xl font-bold">Plan Your Day at The Village</h1>
              <p className="text-muted-foreground text-sm">Choose your experience and start planning your island adventure.</p>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-border sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="combo">Combo Tour</SelectItem>
                <SelectItem value="boating">Boat Tour</SelectItem>
                <SelectItem value="snorkel">Beach & Snorkel</SelectItem>
                <SelectItem value="food">Food Tour</SelectItem>
                <SelectItem value="bar">Beach Bar</SelectItem>
                <SelectItem value="restaurant">Dining</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tours Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : filteredTours.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No tour found matching your criteria.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => { setCategory('all'); setSearchQuery(''); }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTours.map((tour, index) => (
              <Link 
                key={tour.id} 
                to={`/tours/${tour.id}`}
                className="tour-card bg-white rounded-2xl overflow-hidden shadow-sm border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`tour-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={tour.images?.[0] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800'}
                    alt={tour.title}
                    className="w-full h-full object-cover"
                  />
                  {tour.is_popular && (
                    <span className="absolute top-4 left-4 bg-coral text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                  <span className="absolute bottom-4 left-4 glass px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                    {getCategoryIcon(tour.category)}
                    {getCategoryLabel(tour.category)}
                  </span>
                </div>
                
                <div className="p-5">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {tour.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {tour.duration_hours}h
                    </span>
                  </div>
                  
                  <h3 className="font-serif font-bold text-lg text-foreground mb-2 line-clamp-2">
                    {tour.title}
                  </h3>
                  
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {tour.short_description || tour.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground">From</span>
                      <p className="font-bold text-xl text-primary">BSD ${tour.base_price}</p>
                      {tour.child_price && tour.child_price !== tour.base_price && (
                        <p className="text-sm text-muted-foreground">BSD ${tour.child_price} child</p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToursPage;
